import { STATUS_RGB, type Status } from "@/tokens/status";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { fibonacciSphere, latLonToVec3 } from "./fibonacci";
import { HOTSPOTS } from "./hotspots";
import { loadEarthTexture } from "./textures";

interface Props {
  status?: Status;
  size?: number;
}

function parseRgb(rgb: string): [number, number, number] {
  const parts = rgb.split(",").map((n) => Number(n) / 255);
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
}

function statusToHsl(rgb: string): THREE.Color {
  const [r, g, b] = parseRgb(rgb);
  return new THREE.Color(r, g, b);
}

export function StatusGlobe({ status = "healthy", size = 540 }: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<Status>(status);
  statusRef.current = status;

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0.35, 3.4);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(size, size);
    renderer.setClearColor(0x000000, 0);
    el.appendChild(renderer.domElement);

    const [r0, g0, b0] = parseRgb(STATUS_RGB[statusRef.current]);
    const uColor = { value: new THREE.Vector3(r0, g0, b0) };
    const statusColor = statusToHsl(STATUS_RGB[statusRef.current]);

    const root = new THREE.Group();
    root.rotation.z = -0.18;
    root.rotation.x = 0.22;
    scene.add(root);

    const wire = buildWireframe(statusColor);
    root.add(wire);

    const atmosphere = buildAtmosphere(uColor);
    scene.add(atmosphere);

    const fresnelRim = buildFresnelRim(uColor);
    scene.add(fresnelRim);

    const beacons = buildBeacons(statusColor);
    root.add(beacons);

    let earthMap: THREE.Texture | null = null;

    const defaultParticles = buildDefaultParticles(statusColor);
    root.add(defaultParticles);

    loadEarthTexture(
      (t) => {
        earthMap = t;
        root.remove(defaultParticles);
        const { land, ocean } = buildContinentDots(t, statusColor);
        root.add(land);
        root.add(ocean);
      },
      () => {},
    );

    let time = 0;
    let raf = 0;
    const tick = () => {
      time += 0.003;
      root.rotation.y = time;
      const [r, g, b] = parseRgb(STATUS_RGB[statusRef.current]);
      uColor.value.set(r, g, b);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      renderer.dispose();
      if (renderer.domElement.parentNode === el) {
        el.removeChild(renderer.domElement);
      }
      earthMap?.dispose();
    };
  }, [size]);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div
        className="pointer-events-none absolute"
        style={{
          width: size * 1.33,
          height: size * 1.33,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, transparent 36%, rgba(var(--status-rgb), 0.10) 42%, transparent 60%)",
          animation: "globe-pulse 4s ease-in-out infinite",
        }}
      />
      <div
        className="pointer-events-none absolute"
        style={{
          width: size * 1.15,
          height: size * 1.15,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, transparent 44%, rgba(var(--status-rgb), 0.28) 48%, rgba(var(--status-rgb), 0.12) 50%, transparent 54%)",
        }}
      />
      <div
        ref={mountRef}
        className="relative"
        style={{
          width: size,
          height: size,
          opacity: 0,
          animation: "fadeIn 1.4s cubic-bezier(.2,.8,.2,1) 0.3s forwards",
        }}
      />
      <div
        className="pointer-events-none absolute"
        style={{
          bottom: size * 0.08,
          left: "50%",
          transform: "translateX(-50%)",
          width: size * 0.7,
          height: 3,
          borderRadius: "50%",
          background:
            "linear-gradient(90deg, transparent, rgba(232,121,249,0.9) 30%, rgba(255,255,255,0.95) 50%, rgba(232,121,249,0.9) 70%, transparent)",
          boxShadow:
            "0 0 20px rgba(232,121,249,0.8), 0 0 60px rgba(168,85,247,0.4), 0 0 100px rgba(168,85,247,0.2)",
          filter: "blur(1px)",
        }}
      />
    </div>
  );
}

function buildDefaultParticles(statusColor: THREE.Color): THREE.Points {
  const pts = fibonacciSphere(28000);
  const positions = new Float32Array(pts.length * 3);
  const colors = new Float32Array(pts.length * 3);
  pts.forEach(([x, y, z], i) => {
    positions.set([x, y, z], i * 3);
    colors.set([statusColor.r * 0.66, statusColor.g * 0.33, statusColor.b * 0.8], i * 3);
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.PointsMaterial({
    size: 0.01,
    vertexColors: true,
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });
  return new THREE.Points(geo, mat);
}

function buildWireframe(statusColor: THREE.Color): THREE.Mesh {
  const geo = new THREE.SphereGeometry(0.998, 40, 24);
  const mat = new THREE.MeshBasicMaterial({
    color: statusColor,
    wireframe: true,
    transparent: true,
    opacity: 0.09,
    depthWrite: false,
    depthTest: false,
  });
  return new THREE.Mesh(geo, mat);
}

function buildAtmosphere(uColor: { value: THREE.Vector3 }): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: { uColor },
    vertexShader: `
      varying vec3 vNormal;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vNormal;
      uniform vec3 uColor;
      void main() {
        float intensity = pow(0.55 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.5);
        gl_FragColor = vec4(uColor, 1.0) * intensity * 0.22;
      }
    `,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(1.15, 48, 48), mat);
}

function buildFresnelRim(uColor: { value: THREE.Vector3 }): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    side: THREE.FrontSide,
    depthWrite: false,
    uniforms: { uColor },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vWorldPos;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vWorldPos = mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      varying vec3 vNormal;
      varying vec3 vWorldPos;
      void main() {
        vec3 V = normalize(-vWorldPos);
        float fresnel = 1.0 - abs(dot(V, vNormal));
        fresnel = pow(fresnel, 4.0);
        gl_FragColor = vec4(uColor, 1.0) * fresnel * 0.75;
      }
    `,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(1.05, 64, 64), mat);
}

function buildBeacons(statusColor: THREE.Color): THREE.Points {
  const hsl = { h: 0, s: 0, l: 0 };
  statusColor.getHSL(hsl);
  const beaconColor = new THREE.Color().setHSL(hsl.h, hsl.s * 0.85, Math.min(0.82, hsl.l + 0.22));
  const positions = new Float32Array(HOTSPOTS.length * 3);
  const colors = new Float32Array(HOTSPOTS.length * 3);
  HOTSPOTS.forEach((h, i) => {
    const [x, y, z] = latLonToVec3(h.lat, h.lon, 1.015);
    positions.set([x, y, z], i * 3);
    colors.set([beaconColor.r, beaconColor.g, beaconColor.b], i * 3);
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.PointsMaterial({
    size: 0.055,
    vertexColors: true,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });
  return new THREE.Points(geo, mat);
}

function buildContinentDots(
  tex: THREE.Texture,
  statusColor: THREE.Color,
): { land: THREE.Points; ocean: THREE.Points } {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 512;
  const ctx2d = canvas.getContext("2d");
  if (!ctx2d || !tex.image) {
    return { land: new THREE.Points(), ocean: new THREE.Points() };
  }
  ctx2d.drawImage(tex.image as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  const imgData = ctx2d.getImageData(0, 0, canvas.width, canvas.height).data;

  const pts = fibonacciSphere(28000);
  const landP: number[] = [];
  const landC: number[] = [];
  const oceanP: number[] = [];
  const oceanC: number[] = [];

  const hsl = { h: 0, s: 0, l: 0 };
  statusColor.getHSL(hsl);
  const oceanColor = new THREE.Color().setHSL(hsl.h, hsl.s * 0.4, 0.14);

  for (const [x, y, z] of pts) {
    const lat = Math.asin(y) * (180 / Math.PI);
    const lon = Math.atan2(z, -x) * (180 / Math.PI);
    const u = Math.floor(((lon + 180) / 360) * canvas.width);
    const v = Math.floor(((90 - lat) / 180) * canvas.height);
    const idx = (v * canvas.width + u) * 4;
    const r = imgData[idx] ?? 0;
    const g = imgData[idx + 1] ?? 0;
    const b = imgData[idx + 2] ?? 0;
    // Blue marble: ocean is blue-dominant; land is green/brown/white
    const isLand = !(b > r + 12 && b > g + 4);

    if (isLand) {
      landP.push(x, y, z);
      // per-particle hue/lightness variance for organic shimmer
      const c = new THREE.Color().setHSL(
        hsl.h + (Math.random() - 0.5) * 0.025,
        hsl.s,
        hsl.l + (Math.random() - 0.5) * 0.22,
      );
      landC.push(c.r, c.g, c.b);
    } else if (Math.random() < 0.04) {
      // very sparse ocean dots — dimmer, desaturated echo of the status color
      oceanP.push(x, y, z);
      oceanC.push(oceanColor.r, oceanColor.g, oceanColor.b);
    }
  }

  const landGeo = new THREE.BufferGeometry();
  landGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(landP), 3));
  landGeo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(landC), 3));
  const landMat = new THREE.PointsMaterial({
    size: 0.013,
    vertexColors: true,
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });

  const oceanGeo = new THREE.BufferGeometry();
  oceanGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(oceanP), 3));
  oceanGeo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(oceanC), 3));
  const oceanMat = new THREE.PointsMaterial({
    size: 0.008,
    vertexColors: true,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });

  return {
    land: new THREE.Points(landGeo, landMat),
    ocean: new THREE.Points(oceanGeo, oceanMat),
  };
}
